"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { Accordion, Box, Text, Table, Button, Badge, Group, Stack } from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { AcceptOptionModal } from "@/components/options/AcceptOptionModal"
import type { OptionChainRow } from "@/actions/options"

type Props = {
  rows: OptionChainRow[]
  /** Present when the viewer is a logged-in, approved broker — shows Accept buttons inline */
  canAccept?: boolean
}

type CreditTypeGroup = { creditType: string; projects: Map<string, OptionChainRow[]> }

function groupRows(rows: OptionChainRow[]): CreditTypeGroup[] {
  const byCreditType = new Map<string, Map<string, OptionChainRow[]>>()
  for (const row of rows) {
    if (!byCreditType.has(row.creditType)) byCreditType.set(row.creditType, new Map())
    const projects = byCreditType.get(row.creditType)!
    const key = `${row.projectRefId}`
    if (!projects.has(key)) projects.set(key, [])
    projects.get(key)!.push(row)
  }
  return [...byCreditType.entries()].map(([creditType, projects]) => ({ creditType, projects }))
}

export function OptionChainView({ rows, canAccept = false }: Props) {
  const t = useTranslations("options")
  const router = useRouter()
  const [selected, setSelected] = useState<OptionChainRow | null>(null)

  if (rows.length === 0) {
    return <Text c="dimmed" ta="center" py="xl">{t("chainEmpty")}</Text>
  }

  const groups = groupRows(rows)

  return (
    <>
    <Accordion multiple defaultValue={groups.map((g) => g.creditType)}>
      {groups.map((group) => (
        <Accordion.Item key={group.creditType} value={group.creditType}>
          <Accordion.Control>
            <Text fw={600}>{group.creditType}</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              {[...group.projects.entries()].map(([projectRefId, projectRows]) => (
                <Box key={projectRefId}>
                  <Text size="sm" fw={500} c="dimmed" mb={4}>{projectRows[0].projectName}</Text>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t("chainVintage")}</Table.Th>
                        <Table.Th ta="right">{t("chainStrike")}</Table.Th>
                        <Table.Th ta="right">{t("chainMaxDuration")}</Table.Th>
                        <Table.Th ta="right">{t("chainPremium")}</Table.Th>
                        <Table.Th ta="right">{t("chainBlocksAvailable")}</Table.Th>
                        {canAccept && <Table.Th />}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {projectRows.map((row) => (
                        <Table.Tr key={row.refId}>
                          <Table.Td>
                            <Group gap={4}>
                              <Text size="sm">{row.vintage}</Text>
                              <Badge size="xs" variant="light">{row.sellerName}</Badge>
                            </Group>
                          </Table.Td>
                          <Table.Td ta="right">€{row.strikePrice.toFixed(2)}</Table.Td>
                          <Table.Td ta="right">{row.expiryMonths}mo</Table.Td>
                          <Table.Td ta="right">€{row.premiumPerTonne.toFixed(2)}/t</Table.Td>
                          <Table.Td ta="right">{row.remainingTonnes}t</Table.Td>
                          {canAccept && (
                            <Table.Td ta="right">
                              <Button size="xs" variant="light" onClick={() => setSelected(row)}>
                                {t("chainAccept")}
                              </Button>
                            </Table.Td>
                          )}
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Box>
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
    <AcceptOptionModal
      opened={selected !== null}
      listing={selected}
      onClose={() => setSelected(null)}
      onAccepted={() => router.refresh()}
    />
    </>
  )
}
